import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useCart } from '../../contexts/CartProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useSaldo } from '../../contexts/SaldoContext';
import styles from './style.module.scss';
import { FaPix } from 'react-icons/fa6';
import { FiCreditCard, FiShield } from 'react-icons/fi';
import { MdOutlineAccessTimeFilled } from 'react-icons/md';
import CreditoPrePago from '../CreditoPrePago';
import CartaoCredito from '../CartaoCredito';
import { toast } from 'react-toastify';
import Pix from '../Pix';
import logo from '../../assets/logo.png';

const PAYMENT_METHODS = {
  PRE_PAGO: 'prePago',
  CARTAO: 'cartao',
  PIX: 'pix',
};

const PAYMENT_COMPONENTS = {
  [PAYMENT_METHODS.PRE_PAGO]: CreditoPrePago,
  [PAYMENT_METHODS.CARTAO]: CartaoCredito,
  [PAYMENT_METHODS.PIX]: Pix,
};

const StepPagamento = ({ items, handleNextStep, showPrePago = true }) => {
  const { user } = useAuth();
  const { validateCoupon } = useCart();
  const { saldo } = useSaldo();

  const [couponCode, setCouponCode] = useState('');
  const [methodPayment, setMethodPayment] = useState(PAYMENT_METHODS.PRE_PAGO);

  const titleRef = useRef(null);

  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const formatarComoMoeda = (valor) => {
    if (!valor) return 'R$ 0,00';

    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const paymentMethods = useMemo(
    () => [
      {
        id: PAYMENT_METHODS.PRE_PAGO,
        title: 'Crédito Pré-Pago',
        subtitle: 'Use seu saldo da plataforma',
        icon: <img src={logo || '/placeholder.svg'} alt='logo' className={styles.logoIcon} />,
        disabled: !user || user.saldo <= 0,
        balance: user ? formatarComoMoeda(saldo) : 'N/A',
        show: showPrePago,
      },
      {
        id: PAYMENT_METHODS.CARTAO,
        title: 'Cartão de Crédito',
        subtitle: 'Confirmação imediata',
        icon: <FiCreditCard />,
        disabled: false,
        show: true,
      },
      {
        id: PAYMENT_METHODS.PIX,
        title: 'PIX',
        subtitle: 'Confirmação imediata',
        icon: <FaPix />,
        disabled: false,
        show: true,
      },
    ],
    [saldo, showPrePago, user]
  );

  const visibleMethods = useMemo(
    () => paymentMethods.filter((method) => method.show),
    [paymentMethods]
  );

  const orderSummary = useMemo(() => {
    const totalItens = items.reduce((acc, item) => acc + item.quantidade, 0);
    const totalValor = items.reduce((acc, item) => acc + (item.quantidade * item.valor), 0);

    return {
      totalItens,
      totalValor,
    };
  }, [items]);

  useEffect(() => {
    const selectedMethod = visibleMethods.find((method) => method.id === methodPayment);

    if (selectedMethod && !selectedMethod.disabled) {
      return;
    }

    const firstEnabledMethod = visibleMethods.find((method) => !method.disabled);

    if (firstEnabledMethod) {
      setMethodPayment(firstEnabledMethod.id);
      return;
    }

    if (visibleMethods[0]) {
      setMethodPayment(visibleMethods[0].id);
    }
  }, [methodPayment, visibleMethods]);

  const validarCupon = async (isPix = false) => {
    const normalizedCoupon = couponCode.trim().toLowerCase();
    const isPix10Coupon = normalizedCoupon === 'pix10';

    if (isPix10Coupon && !isPix) {
      toast.warn('Cupom não aplicável a esta modalidade de pagamento', {
        position: 'top-right',
        autoClose: 5000,
        theme: 'light',
      });

      return false;
    }

    return validateCoupon(couponCode);
  };

  const handleMethodPayment = (method) => {
    setMethodPayment(method);
  };

  const renderPaymentStep = () => {
    const StepComponent = PAYMENT_COMPONENTS[methodPayment] || Pix;

    return (
      <StepComponent
        validarCupon={validarCupon}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        items={items}
        handleNextStep={handleNextStep}
      />
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 ref={titleRef} className={styles.title}>
          Escolha a forma de pagamento
        </h1>
        <p className={styles.subtitle}>
          Selecione o método de pagamento de sua preferência
        </p>
      </div>

      <div className={styles.checkoutInfo}>
        <div className={styles.summaryBox}>
          <span className={styles.summaryLabel}>Resumo rápido</span>
          <strong>{orderSummary.totalItens} {orderSummary.totalItens === 1 ? 'item' : 'itens'}</strong>
          <span className={styles.summaryValue}>{formatarComoMoeda(orderSummary.totalValor)}</span>
        </div>

        <div className={styles.trustItem}>
          <FiShield />
          <span>Pagamento seguro e protegido</span>
        </div>

        <div className={styles.trustItem}>
          <MdOutlineAccessTimeFilled />
          <span>Confirmação rápida após a aprovação</span>
        </div>
      </div>

      <div className={styles.paymentMethods}>
        {visibleMethods.map((method) => (
          <button
            key={method.id}
            disabled={method.disabled}
            className={`${styles.paymentCard} ${
              methodPayment === method.id ? styles.selected : ''
            } ${method.disabled ? styles.disabled : ''}`}
            onClick={() => !method.disabled && handleMethodPayment(method.id)}
            aria-pressed={methodPayment === method.id}
          >
            <div className={styles.cardIcon}>{method.icon}</div>

            <div className={styles.cardContent}>
              <h3 className={styles.cardTitle}>{method.title}</h3>
              <p className={styles.cardSubtitle}>{method.subtitle}</p>
              {method.balance && (
                <div className={styles.balance}>
                  <span className={styles.balanceLabel}>Saldo atual:</span>
                  <span className={styles.balanceValue}>{method.balance}</span>
                </div>
              )}
            </div>

            {methodPayment === method.id && (
              <div className={styles.selectedIndicator}>
                <div className={styles.checkmark}>✓</div>
              </div>
            )}
          </button>
        ))}
      </div>

      <div className={styles.paymentContent}>{renderPaymentStep()}</div>
    </div>
  );
};

export default StepPagamento;
